/**
 * Feature-level error boundary component
 * Provides graceful degradation for specific features without breaking the entire page
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { Button } from '../ui/button';
import { reportErrorFromBoundary } from '../../monitoring/errorReporting';
import { errorLogger } from '../../utils/loggerInstance';
import { generateTempId } from '../../utils/uuid';

interface FeatureErrorBoundaryProps {
  children: ReactNode;
  featureName: string;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showErrorDetails?: boolean;
  allowDismiss?: boolean;
  resetKeys?: ReadonlyArray<string | number | boolean>;
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string;
  retryCount: number;
  isDismissed: boolean;
}

export class FeatureErrorBoundary extends Component<
  FeatureErrorBoundaryProps,
  FeatureErrorBoundaryState
> {
  public state: FeatureErrorBoundaryState = {
    hasError: false,
    error: null,
    errorId: '',
    retryCount: 0,
    isDismissed: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<FeatureErrorBoundaryState> {
    const errorId = generateTempId('feature_error');

    return {
      hasError: true,
      error,
      errorId,
      isDismissed: false,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, featureName } = this.props;

    errorLogger.error('Feature error boundary caught an error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      featureName,
      url: window.location.href,
    });

    reportErrorFromBoundary(error, {
      boundary: 'feature',
      featureName,
      errorId: this.state.errorId,
      componentStack: errorInfo.componentStack,
    });

    if (onError) {
      onError(error, errorInfo);
    }
  }

  public componentDidUpdate(prevProps: FeatureErrorBoundaryProps) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when reset keys change
    if (hasError && prevProps.resetKeys !== resetKeys && resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => prevProps.resetKeys?.[index] !== key,
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  private readonly resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorId: '',
      retryCount: 0,
      isDismissed: false,
    });
  };

  private readonly handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorId: '',
      retryCount: prevState.retryCount + 1,
      isDismissed: false,
    }));

    errorLogger.info('Feature error boundary retrying', {
      retryCount: this.state.retryCount + 1,
      featureName: this.props.featureName,
    });
  };

  private readonly handleDismiss = () => {
    this.setState({ isDismissed: true });
  };

  public render() {
    const { hasError, error, retryCount, isDismissed } = this.state;
    const { children, featureName, fallback, showErrorDetails, allowDismiss } = this.props;

    if (hasError && !isDismissed) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      const canRetry = retryCount < 3;

      return (
        <div className='relative bg-red-50 border border-red-200 rounded-lg p-4 my-2'>
          {allowDismiss && (
            <button
              onClick={this.handleDismiss}
              className='absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 transition-colors'
              aria-label='Dismiss error'
            >
              <X className='w-4 h-4' />
            </button>
          )}

          <div className='flex items-start gap-3'>
            <div className='flex-shrink-0'>
              <AlertTriangle className='w-5 h-5 text-red-500 mt-0.5' />
            </div>

            <div className='flex-1 min-w-0'>
              <h3 className='text-sm font-medium text-red-800 mb-1'>{featureName} Unavailable</h3>

              <p className='text-sm text-red-700 mb-3'>
                This feature is temporarily unavailable. You can continue using other parts of the
                application.
              </p>

              <div className='flex flex-wrap gap-2'>
                {canRetry && (
                  <Button
                    onClick={this.handleRetry}
                    size='sm'
                    variant='outline'
                    className='text-red-700 border-red-300 hover:bg-red-100'
                  >
                    <RefreshCw className='w-3 h-3 mr-1' />
                    Retry ({3 - retryCount} left)
                  </Button>
                )}
              </div>

              {showErrorDetails && process.env.NODE_ENV === 'development' && error && (
                <details className='mt-3'>
                  <summary className='cursor-pointer text-xs text-red-600 hover:text-red-800'>
                    Error details (development only)
                  </summary>
                  <pre className='mt-2 p-2 bg-red-100 border border-red-200 rounded text-xs overflow-auto max-h-24'>
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    // If dismissed, render nothing (graceful degradation)
    if (hasError && isDismissed) {
      return null;
    }

    return children;
  }
}

export default FeatureErrorBoundary;
